import { reducedMotion } from './motion'
import { el } from './ui'

// Dragging on a computer, made to feel like picking something up (Liam, 12 Oct 2026, the computer Diary: "animations for
// dragging isnt that fun"). The browser's own drag picture is a faint, see-through photo stuck to the pointer. Instead
// the browser is handed an empty picture and Roundly draws the card itself: it lifts out of its row, follows the pointer
// with a little lag and leans the way it is moving; let go on a day it drops into it, let go anywhere else it flies back
// to where it came from. The drag itself (what can be dropped where, and what a drop does) is unchanged: this is only
// the picture. Reduced motion keeps the browser's own picture.

const BLANK = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
let blank: HTMLImageElement | null = null
const blankImage = (): HTMLImageElement | null => {
  if (typeof Image === 'undefined') return null
  if (!blank) { blank = new Image(); blank.src = BLANK }
  return blank.complete ? blank : null
}
// Loaded now, so it is ready by the first drag (an image still loading would show nothing and the browser's own instead).
blankImage()

const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n))

// Takes over the drag's picture for `row`. False when it cannot (reduced motion, no image yet): the caller draws its own.
export function flyDrag(e: DragEvent, row: HTMLElement): boolean {
  const img = blankImage()
  if (!e.dataTransfer || !img || reducedMotion() || typeof requestAnimationFrame !== 'function') return false
  const rect = row.getBoundingClientRect()
  const width = Math.min(Math.max(rect.width, 180), 340)
  const card = row.cloneNode(true) as HTMLElement
  card.removeAttribute('id')
  card.classList.remove('first-visit-glow', 'tray-row-moving', 'guide-glow')
  card.classList.add('drag-fly')
  card.style.width = `${width}px`
  const layer = el('div', { class: 'drag-fly-layer', 'aria-hidden': 'true' }, card)
  document.body.append(layer)
  e.dataTransfer.setDragImage(img, 0, 0)

  const grabX = clamp(e.clientX - rect.left, 10, width - 10)
  const grabY = clamp(e.clientY - rect.top, 10, Math.max(10, card.offsetHeight - 10))
  // It starts where the row is and catches up with the pointer, so it is seen to lift out of the list.
  let x = rect.left
  let y = rect.top
  let tx = e.clientX - grabX
  let ty = e.clientY - grabY
  let lastX = e.clientX
  let lean = 0
  let leanTo = 0
  let lift = 0
  let frame = 0
  let done = false
  const paint = (): void => {
    card.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${lean - 1.5 * lift}deg) scale(${1 + 0.04 * lift})`
  }
  const step = (): void => {
    x += (tx - x) * 0.32
    y += (ty - y) * 0.32
    lean += (leanTo - lean) * 0.2
    leanTo *= 0.86
    lift += (1 - lift) * 0.25
    paint()
    frame = requestAnimationFrame(step)
  }
  paint()
  frame = requestAnimationFrame(step)
  const over = (ev: DragEvent): void => {
    // Some browsers send a last move at 0,0 as the drag ends: not a place the pointer went.
    if (ev.clientX === 0 && ev.clientY === 0) return
    leanTo = clamp((ev.clientX - lastX) * 0.7, -9, 9)
    lastX = ev.clientX
    tx = ev.clientX - grabX
    ty = ev.clientY - grabY
  }
  let dropped = false
  // A drop ends it even when the row it came from has been redrawn away (its dragend then reaches nobody).
  const onDrop = (): void => { dropped = true; window.setTimeout(() => finish(), 60) }
  const finish = (ev?: DragEvent): void => {
    if (done) return
    done = true
    cancelAnimationFrame(frame)
    document.removeEventListener('dragover', over, true)
    document.removeEventListener('drop', onDrop, true)
    document.removeEventListener('dragend', finish, true)
    row.removeEventListener('dragend', finish)
    for (const t of ['mousemove', 'pointerdown', 'keydown'] as const) document.removeEventListener(t, over2, true)
    const landed = dropped || (ev?.dataTransfer?.dropEffect ?? 'none') !== 'none'
    const from = `translate3d(${x}px, ${y}px, 0) rotate(${lean - 1.5}deg) scale(1.04)`
    // Back to where the row is now (the page may have scrolled while it was carried).
    const home = row.isConnected ? row.getBoundingClientRect() : rect
    // Dropped on a day: it settles into it, shrinking away. Let go anywhere else: it flies back to its place.
    const to = landed
      ? `translate3d(${x + width * 0.25}px, ${y + 6}px, 0) rotate(0deg) scale(0.5)`
      : `translate3d(${home.left}px, ${home.top}px, 0) rotate(0deg) scale(1)`
    if (typeof card.animate !== 'function') { layer.remove(); return }
    const run = card.animate([{ transform: from, opacity: 1 }, { transform: to, opacity: landed ? 0 : 0.9 }], { duration: landed ? 220 : 300, easing: landed ? 'cubic-bezier(.4,0,.9,.6)' : 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' })
    run.onfinish = (): void => layer.remove()
    // Never left on screen, whatever happens to the animation.
    window.setTimeout(() => layer.remove(), 600)
  }
  // A drag never lets the pointer move or a key press reach the page while it lasts: the first one after it means it is
  // over, even when its dragend reached nobody (the row redrawn away; review, 12 Oct 2026: a card left frozen).
  const over2 = (): void => finish()
  window.setTimeout(() => { if (done) return; for (const t of ['mousemove', 'pointerdown', 'keydown'] as const) document.addEventListener(t, over2, { capture: true, once: true }) }, 0)
  document.addEventListener('dragover', over, true)
  document.addEventListener('drop', onDrop, true)
  // The row can be redrawn away by the drop before its own dragend; the page hears the end either way.
  document.addEventListener('dragend', finish, true)
  row.addEventListener('dragend', finish)
  return true
}
